import { useState } from "react";
import { Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { TASK_DEPARTMENTS, TASK_PRIORITIES, TASK_STATUSES, type TaskPriority, type TaskStatus } from "./tasksConstants";
import { AssigneesMultiSelect } from "./AssigneesMultiSelect";
import { statusDotClass } from "./tasksUi";

export type NewTaskRow = {
  id: string;
  task_number: number;
  title: string;
  assignees: string[];
  department: string;
  priority: TaskPriority;
  status: TaskStatus;
  created_at: string;
  updated_at: string;
  due_date: string | null;
  created_by_email: string | null;
};

type Props = {
  submitLabel?: string;
  onSaved: (row: NewTaskRow) => void;
  onCancel?: () => void;
};

export function TasksNewTaskForm({ submitLabel = "Save task", onSaved, onCancel }: Props) {
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState<string>(TASK_DEPARTMENTS[0]);
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [status, setStatus] = useState<TaskStatus>("to-do");
  const [assignees, setAssignees] = useState<Set<string>>(new Set());
  const [subtasksText, setSubtasksText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const t = title.trim();
    if (!t) {
      setError("Enter a task title.");
      return;
    }

    setSaving(true);
    setError(null);

    const subtasks = subtasksText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 50);

    const { data, error } = await supabase
      .from("tasks")
      .insert({
        title: t,
        department,
        priority,
        status,
        assignees: Array.from(assignees),
        due_date: dueDate.trim() ? dueDate.trim() : null,
      })
      .select(
        "id,task_number,title,assignees,department,priority,status,created_at,updated_at,due_date,created_by_email"
      )
      .single();

    if (error) {
      setSaving(false);
      setError(error.message);
      return;
    }

    if (subtasks.length) {
      const { error: stErr } = await supabase
        .from("task_subtasks")
        .insert(subtasks.map((title) => ({ task_id: (data as NewTaskRow).id, title })));
      if (stErr) {
        setSaving(false);
        setError(stErr.message);
        return;
      }
    }

    setSaving(false);
    onSaved(data as NewTaskRow);
  }

  return (
    <form
      className="grid grid-cols-1 gap-3 md:grid-cols-12"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="md:col-span-6">
        <label className="block text-sm font-semibold">Task</label>
        <textarea
          name="title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (error === "Enter a task title.") setError(null);
          }}
          placeholder="Add a new task…"
          rows={2}
          className="mt-2 w-full min-h-[4.5rem] resize-y rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm leading-snug outline-none ring-2 ring-transparent focus:ring-brand-maroon break-words"
          autoFocus
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold">Department</label>
        <select
          name="department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className="mt-2 w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
        >
          {TASK_DEPARTMENTS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold">Priority</label>
        <select
          name="priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as TaskPriority)}
          className="mt-2 w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
        >
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p === "high" ? "High" : "Normal"}
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-semibold">Status</label>
        <div className="relative mt-2">
          <span
            className={[
              "pointer-events-none absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full ring-2 ring-white/80",
              statusDotClass(status),
            ].join(" ")}
            aria-hidden="true"
          />
          <select
            name="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as TaskStatus)}
            className="w-full rounded-xl bg-brand-gray/40 py-2.5 pl-9 pr-3 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
          >
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="md:col-span-4">
        <label className="block text-sm font-semibold">Deadline (optional)</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="mt-2 w-full rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
        />
      </div>

      <div className="relative z-40 md:col-span-8">
        <label className="block text-sm font-semibold">Assigned</label>
        <AssigneesMultiSelect
          value={Array.from(assignees)}
          onChange={(next) => setAssignees(new Set(next))}
        />
      </div>

      <div className="md:col-span-12">
        <label className="block text-sm font-semibold">Subtasks (optional)</label>
        <textarea
          value={subtasksText}
          onChange={(e) => setSubtasksText(e.target.value)}
          placeholder={"One per line, e.g.\nCall venue\nConfirm budget\nSend invite"}
          rows={3}
          className="mt-2 w-full resize-none rounded-xl bg-brand-gray/40 px-3 py-2.5 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
        />
        <p className="mt-1 text-xs text-brand-blue/60">Tip: one subtask per line. (Max 50)</p>
      </div>

      <div className="flex flex-col gap-2 md:col-span-12 md:flex-row md:items-center md:justify-end">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex w-full items-center justify-center rounded-full bg-brand-gray px-6 py-3 text-sm font-bold text-brand-blue ring-1 ring-black/10 md:w-auto"
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-maroon px-6 py-3 text-sm font-bold uppercase tracking-wide text-white enabled:hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-45 md:w-auto"
        >
          <Plus className="h-4 w-4" />
          {saving ? "Saving…" : submitLabel}
        </button>
      </div>

      {error ? (
        <p className="md:col-span-12 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
          {error}
        </p>
      ) : null}
    </form>
  );
}
